import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from 'reactstrap';
import { CheckCircle, XCircle, Trash2, Edit, FileText, Lock } from 'lucide-react';
import { AppContext } from '../application/provider';
import * as SolicitudViaticoService from '../axios_services/viaticosolicitud.service';
import Loader from '../components/Loader/Loader';
import { ButtonInsert, ButtonSearch, ButtonUpdate, ButtonDelete } from '../components/Buttons/Buttons';
import TabsWithTable from '../components/Tabs/TabsWithTable';
import ModalConfirmar from '../components/Modal/ModalConfirmar';
import ModalSolicitudViatico from '../components/Modal/ModalSolicitudViatico';
import ModalValidarSolicitudViatico from '../components/Modal/ModalValidarSolicitudViatico';
import ModalDetallePlanillaViatico from '../components/Modal/ModalDetallePlanillaViatico';
import '../components/Tablas/AccionesProspecto.css';
import './PapeletaSalida.css';

const ESTADO_LABELS = { 
    1: 'PENDIENTE DE JEFE INMEDIATO', 
    2: 'APROBADO POR JEFE (PENDIENTE SUBGERENCIA)', 
    3: 'RECHAZADO POR JEFE',
    4: 'APROBADO POR SUBGERENCIA',
    5: 'RECHAZADO POR SUBGERENCIA' 
};
const REPORTE_PERFILES = [8, 26];

const now = new Date();
const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const getRowStyle = (validacionId) => {
    const e = Number(validacionId);
    if (e === 1) return { backgroundColor: '#fef3c7' };
    if (e === 2) return { backgroundColor: '#dbeafe' };
    if (e === 3) return { backgroundColor: '#fee2e2' };
    if (e === 4) return { backgroundColor: '#dcfce7' };
    if (e === 5) return { backgroundColor: '#fef2f2' };
    return {};
};

function SolicitudViaticos() {
    const [state] = useContext(AppContext);

    const userId = Number(state.user?.usuario_id);
    const perfilId = Number(state.user?.perfil_id);
    const esAdmin = perfilId === 8;
    const esReporte = REPORTE_PERFILES.includes(perfilId);

    const [loading, setLoading] = useState(false);
    const [reporteSolicitudes, setReporteSolicitudes] = useState([]);
    const [solicitudes, setSolicitudes] = useState([]);

    const [periodoInput, setPeriodoInput] = useState(mesActual);
    const [periodo, setPeriodo] = useState(mesActual);

    const [modalCrear, setModalCrear] = useState(false);
    const [solicitudToEdit, setSolicitudToEdit] = useState(null);

    const [confirmDelete, setConfirmDelete] = useState(null);

    const [modalValidar, setModalValidar] = useState(false);
    const [solicitudValidar, setSolicitudValidar] = useState(null);
    const [accionValidar, setAccionValidar] = useState(null); // 2: Aprobar, 3: Rechazar

    const [modalPlanilla, setModalPlanilla] = useState(false);
    const [planillaSolicitudId, setPlanillaSolicitudId] = useState(null);

    const initialFetch = useRef(false);

    useEffect(() => {
        if (!initialFetch.current && perfilId) {
            loadAll();
            initialFetch.current = true;
        }
    }, [perfilId]);

    const loadAll = async () => {
        setPeriodo(periodoInput);
        setLoading(true);
        try {
            const promises = [SolicitudViaticoService.getSolicitudes()];
            if (esReporte) promises.push(SolicitudViaticoService.getReporteSolicitudes());
            const [res, resReporte] = await Promise.all(promises);
            setSolicitudes(res.data || []);
            if (resReporte) setReporteSolicitudes(resReporte.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await SolicitudViaticoService.deleteSolicitud({ solicitud_id: confirmDelete.solicitud_id });
            loadAll();
        } catch (error) {
            alert(error.response?.data?.error || 'Error al eliminar la solicitud');
        } finally {
            setConfirmDelete(null);
        }
    };

    const filtrarPorMes = (lista, mes) => {
        const filtrada = mes ? lista.filter(s => s.fecha_registro && s.fecha_registro.startsWith(mes)) : lista;
        return [...filtrada].sort((a, b) => {
            const ca = a.fecha_registro || '';
            const cb = b.fecha_registro || '';
            return cb.localeCompare(ca);
        });
    };

    const solicitudesFiltradas = filtrarPorMes(solicitudes, periodo);

    const misSolicitudes = solicitudesFiltradas.filter(s => Number(s.usuario_id) === userId);
    const bandejaJefe = solicitudesFiltradas.filter(s => Number(s.jefe_perfil_id) === perfilId);
    const todasAdmin = solicitudesFiltradas;
    const reporteFiltrado = filtrarPorMes(reporteSolicitudes, periodo);

    const renderTabla = (rows, mostrarColab = false, esJefe = false, esSubgerencia = false) => {
        return (
            <div className="table-container" style={{ overflowX: 'auto', background: 'white' }}>
                <Table hover size="sm" className="mb-0" responsive>
                    <thead>
                        <tr>
                            {mostrarColab && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '150px', width: '150px' }}>COLABORADOR</th>}
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '130px', width: '130px' }}>FECHA SOLICITUD</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '200px', width: '200px' }}>ASUNTO</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '350px', width: '350px' }}>DETALLE SOLICITUD</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '150px', width: '150px' }}>ESTADO</th>
                            {mostrarColab && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '150px', width: '150px' }}>APROBADOR</th>}
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '180px', width: '180px' }}>OBSERVACIÓN</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '120px', width: '120px' }}>DETALLES</th>
                            <th className="bg-gray-700 text-white uppercase">ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows.map((s) => {
                            const validacionId = Number(s.validacion_id);

                            const sMes = s.fecha_registro ? s.fecha_registro.substring(0, 7) : '';
                            const isCurrentMonth = sMes === mesActual;

                            const canEditDelete = validacionId === 1 && (Number(s.usuario_id) === userId || esAdmin);
                            const canApproveJefe = esJefe && validacionId === 1 && isCurrentMonth;
                            const canApproveSubgerencia = esSubgerencia && validacionId === 2 && s.planilla_id;
                            const canApprove = canApproveJefe || canApproveSubgerencia;

                            return (
                                <tr key={s.solicitud_id} style={getRowStyle(s.validacion_id)}>
                                    {mostrarColab && <td>{s.nombre_empleado?.toUpperCase()}</td>}
                                    <td>{s.fecha_registro}</td>
                                    <td className="font-bold">{s.asunto?.toUpperCase() || '—'}</td>
                                    <td>{s.detalle_solicitud?.toUpperCase()}</td>
                                    <td>{(ESTADO_LABELS[validacionId] || '—')}</td>
                                    {mostrarColab && <td>{s.nombre_aprobador ? s.nombre_aprobador.toUpperCase() : '—'}</td>}
                                    <td style={{ maxWidth: '180px', fontSize: '11px', color: validacionId === 3 ? '#991b1b' : '#374151' }}>
                                        {s.detalle?.toUpperCase() || 'SIN OBSERVACIONES'}
                                    </td>
                                    <td className="p-2 text-center align-middle">
                                        <div className="flex justify-center">
                                            <button 
                                                className="boton-accion"
                                                title={s.planilla_id ? "Ver Detalles Planilla" : "Aún no se ha generado planilla"} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (s.planilla_id) {
                                                        setPlanillaSolicitudId(s.solicitud_id);
                                                        setModalPlanilla(true);
                                                    }
                                                }}
                                                style={{ cursor: s.planilla_id ? 'pointer' : 'not-allowed', opacity: s.planilla_id ? 1 : 0.6 }}
                                                disabled={!s.planilla_id}
                                            >
                                                <FileText color={s.planilla_id ? "#16a34a" : "#dc2626"} size={22} />
                                            </button>
                                        </div>
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <div className="acciones-container">
                                            {canEditDelete && (
                                                <>
                                                    <ButtonUpdate onClick={() => {
                                                        setSolicitudToEdit(s);
                                                        setModalCrear(true);
                                                    }} />
                                                    <ButtonDelete onClick={() => setConfirmDelete(s)} />
                                                </>
                                            )}
                                            {canApprove && (
                                                <>
                                                    <button className="boton-accion" title="Aprobar" onClick={() => {
                                                        setSolicitudValidar(s);
                                                        setAccionValidar(canApproveSubgerencia ? 4 : 2);
                                                        setModalValidar(true);
                                                    }}>
                                                        <CheckCircle color="#28a745" size={22} />
                                                    </button>
                                                    <button className="boton-accion" title="Rechazar" onClick={() => {
                                                        setSolicitudValidar(s);
                                                        setAccionValidar(canApproveSubgerencia ? 5 : 3);
                                                        setModalValidar(true);
                                                    }}>
                                                        <XCircle color="#dc3545" size={22} />
                                                    </button>
                                                </>
                                            )}
                                            {(!canEditDelete && !canApprove && [2, 4].includes(Number(s.validacion_id))) && (
                                                <span className="text-2xl" title="Validado y bloqueado" style={{ cursor: 'not-allowed', display: 'flex', alignItems: 'center' }}>
                                                    🔒
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan="10" className="text-center text-gray-500 py-4">No hay solicitudes registradas.</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>
        );
    };

    const pendientesJefe = bandejaJefe.filter(s => Number(s.validacion_id) === 1).length;

    const tabs = [
        { key: 'mis_solicitudes', label: 'Mis Solicitudes', content: renderTabla(misSolicitudes, false, false) },
    ];

    if (!esAdmin) {
        tabs.push({
            key: 'jefe',
            label: `Bandeja de Aprobación${pendientesJefe > 0 ? ` (${pendientesJefe})` : ''}`,
            content: renderTabla(bandejaJefe, true, true)
        });
    }

    if (esAdmin) {
        tabs.push({ key: 'todas', label: 'Todas las Solicitudes', content: renderTabla(todasAdmin, true, false) });
    }

    if (esReporte) {
        tabs.push({ key: 'reporte', label: `Reporte de Solicitudes${reporteFiltrado.length > 0 ? ` (${reporteFiltrado.length})` : ''}`, content: renderTabla(reporteFiltrado, true, false, perfilId === 26) });
    }

    const defaultTab = pendientesJefe > 0 ? 'jefe' : 'mis_solicitudes';
    const [tabActiva, setTabActiva] = useState(defaultTab);

    return (
        <div className="caja-flujo-container">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>SOLICITUD DE VIÁTICOS</h1>
            </div>

            <div className="filtros" style={{ marginBottom: '10px' }}>
                <div>
                    <label>Mes:</label>
                    <input type="month" value={periodoInput} onChange={e => setPeriodoInput(e.target.value)} />
                </div>
            </div>

            <div className="mb-4">
                <ButtonSearch onClick={loadAll} isLoading={loading} />
            </div>

            {(tabActiva === 'mis_solicitudes' || esAdmin) && (
                <div className="nuevo-movimiento-section mb-3">
                    <ButtonInsert onClick={() => {
                        setSolicitudToEdit(null);
                        setModalCrear(true);
                    }} />
                </div>
            )}

            <TabsWithTable tabs={tabs} onTabChange={setTabActiva} defaultTab={defaultTab} />

            <ModalSolicitudViatico
                isOpen={modalCrear}
                onClose={() => { setModalCrear(false); setSolicitudToEdit(null); }}
                onRefresh={loadAll}
                currentUserId={userId}
                solicitudToEdit={solicitudToEdit}
            />

            <ModalValidarSolicitudViatico
                isOpen={modalValidar}
                onClose={() => { setModalValidar(false); setSolicitudValidar(null); }}
                onRefresh={loadAll}
                solicitud={solicitudValidar}
                accion={accionValidar}
            />

            <ModalDetallePlanillaViatico 
                isOpen={modalPlanilla}
                onClose={() => { setModalPlanilla(false); setPlanillaSolicitudId(null); }}
                solicitudId={planillaSolicitudId}
            />

            <ModalConfirmar
                isOpen={!!confirmDelete}
                titulo="Confirmar Eliminación"
                mensaje="¿Está seguro que desea eliminar esta solicitud?"
                textConfirmar="Eliminar"
                textCancelar="Cancelar"
                variante="peligro"
                onConfirmar={handleDelete}
                onCancelar={() => setConfirmDelete(null)}
            />
        </div>
    );
}

export default SolicitudViaticos;
